import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, InfoIcon } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SchedulingWidget } from '../components/SchedulingWidget';
export function ContactPage() {
  return <div className="w-full bg-brandbg min-h-screen pb-20" data-id="element-212">
      {/* Header */}
      <section className="bg-white border-b border-bordercolor py-12 md:py-16" data-id="element-213">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-214">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} data-id="element-215">
            <h1 className="text-3xl md:text-4xl font-bold text-brandtext mb-4" data-id="element-216">
              Contact & Appointments
            </h1>
            <p className="text-lg text-brandtext-muted max-w-2xl" data-id="element-217">
              Schedule your initial telehealth evaluation or reach out to our
              administrative team with general questions.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-218">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-219">
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5 space-y-8" data-id="element-220">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-surface border border-bordercolor rounded-xl p-6 md:p-8 shadow-subtle" data-id="element-221">
              <h2 className="text-xl font-bold text-brandtext mb-6" data-id="element-222">
                Clinic Information
              </h2>

              <div className="space-y-6" data-id="element-223">
                <div className="flex items-start gap-4" data-id="element-224">
                  <div className="mt-1 text-primary" data-id="element-225">
                    <MapPinIcon className="w-5 h-5" data-id="element-226" />
                  </div>
                  <div data-id="element-227">
                    <h3 className="font-medium text-brandtext" data-id="element-228">
                      [CLINIC NAME]
                    </h3>
                    <p className="text-sm text-brandtext-muted mt-1" data-id="element-229">
                      Telehealth services exclusively available for residents of
                      [STATE].
                      <br data-id="element-230" />
                      [CITY], [STATE]
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-231">
                  <div className="mt-1 text-primary" data-id="element-232">
                    <PhoneIcon className="w-5 h-5" data-id="element-233" />
                  </div>
                  <div data-id="element-234">
                    <h3 className="font-medium text-brandtext" data-id="element-235">Phone</h3>
                    <p className="text-sm text-brandtext-muted mt-1" data-id="element-236">[PHONE]</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-237">
                  <div className="mt-1 text-primary" data-id="element-238">
                    <MailIcon className="w-5 h-5" data-id="element-239" />
                  </div>
                  <div data-id="element-240">
                    <h3 className="font-medium text-brandtext" data-id="element-241">Email</h3>
                    <p className="text-sm text-brandtext-muted mt-1" data-id="element-242">[EMAIL]</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-243">
                  <div className="mt-1 text-primary" data-id="element-244">
                    <ClockIcon className="w-5 h-5" data-id="element-245" />
                  </div>
                  <div data-id="element-246">
                    <h3 className="font-medium text-brandtext" data-id="element-247">
                      Administrative Hours
                    </h3>
                    <p className="text-sm text-brandtext-muted mt-1" data-id="element-248">
                      Monday - Friday: 9:00 AM - 5:00 PM
                      <br data-id="element-249" />
                      Closed Weekends and Holidays
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Existing Patient Notice */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-primary-light border border-primary/20 rounded-xl p-6 flex items-start gap-4" data-id="element-250">
              <InfoIcon className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" data-id="element-251" />
              <div className="text-sm text-primary-dark" data-id="element-252">
                <strong className="block mb-1" data-id="element-253">Established Patients</strong>
                For medical questions, prescription refills, or to share lab
                results, please use the secure patient portal. Do not use the
                forms on this website for medical communication.
              </div>
            </motion.div>
          </div>

          {/* Right Column: Scheduling & Form */}
          <div className="lg:col-span-7 space-y-12" data-id="element-254">
            {/* Scheduling Widget */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-255">
              <div className="mb-6" data-id="element-256">
                <h2 className="text-2xl font-bold text-brandtext" data-id="element-257">
                  Appointment Request
                </h2>
                <p className="text-brandtext-muted mt-2" data-id="element-258">
                  Select a time for your initial telehealth evaluation.
                </p>
              </div>
              <SchedulingWidget data-id="element-259" />
            </motion.div>

            <hr className="border-bordercolor" data-id="element-260" />

            {/* General Contact Form */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-261">
              <div className="mb-6" data-id="element-262">
                <h2 className="text-2xl font-bold text-brandtext" data-id="element-263">
                  General Inquiries
                </h2>
                <p className="text-brandtext-muted mt-2" data-id="element-264">
                  Have a question before scheduling? Send our administrative
                  team a message.
                </p>
              </div>
              <ContactForm data-id="element-265" />
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}